import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { accountApi } from '@/api/account.api';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import type { EligibleFundingSource } from '@/types/account.types';

/**
 * Above this many sources the list stops being a choice and becomes a search.
 *
 * A native select has no search worth the name: the browser matches only the start of the option
 * text, and these options lead with the GL account code — the one thing nobody memorises. On a
 * multi-branch company with dozens of accounts that is a scroll hunt through truncated labels.
 * Below the threshold the select is still the faster control, and a small client keeps its single
 * click, so both are kept rather than forcing one on everybody.
 */
const SEARCHABLE_ABOVE = 8;

export interface FundingSourceValue {
  /** Set when the chosen source is a bank account. Posting resolves the GL account through it. */
  bankAccountId: string | null;
  /** Set when the chosen source is a cash GL account — the only way to reach a till. */
  fundingGlAccountId: string | null;
  /**
   * Set alongside fundingGlAccountId when the source is a petty cash float, and it says WHICH box
   * (GAP-012). The account posts; the float is what actually loses the cash.
   */
  pettyCashFloatId?: string | null;
}

interface Props {
  paymentMethod: string;
  /** True on a payment (money out), false on a receipt (money in). */
  forAp: boolean;
  value: FundingSourceValue;
  onChange: (value: FundingSourceValue) => void;
  locationId?: string | null;
  currencyCode?: string | null;
  disabled?: boolean;
  className?: string;
}

/**
 * Picks where the money moves — the bank account for bank methods, the cash account for cash
 * (GAP-002, GAP-013).
 *
 * Shared by the payment and receipt forms so the two cannot drift apart. Before this, both forms
 * offered every bank account whatever the method, and cash had no route to a till at all.
 *
 * This filters; it does not enforce. The server validates the same choice on save, so an operator
 * who works around the list — or a caller that skips the UI entirely — is still refused.
 */
export function FundingSourceSelect({
  paymentMethod, forAp, value, onChange,
  locationId, currencyCode, disabled, className,
}: Props) {
  const [sources, setSources] = useState<EligibleFundingSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!paymentMethod) { setSources([]); return; }

    // The method decides the whole list, so a stale response for the previous method would offer
    // accounts the current method cannot use. Ignore anything that arrives after a change.
    let current = true;
    setLoading(true);
    setError(null);

    accountApi.eligibleFunding({ paymentMethod, forAp, locationId, currencyCode })
      .then(res => {
        if (!current) return;
        setSources(res.data.data ?? []);
      })
      .catch(() => {
        if (!current) return;
        setSources([]);
        setError('Could not load accounts. Reload the page, or contact your administrator if this continues.');
      })
      .finally(() => { if (current) setLoading(false); });

    return () => { current = false; };
  }, [paymentMethod, forAp, locationId, currencyCode]);

  // What identifies a source in the list. A float, a bank account and a bare cash account are each
  // identified by a different field, and using the GL account for all three would make two floats
  // on one control account indistinguishable.
  const keyOf = (s: EligibleFundingSource) =>
    s.pettyCashFloatId ?? s.bankAccountId ?? s.accountId;

  const cleared: FundingSourceValue =
    { bankAccountId: null, fundingGlAccountId: null, pettyCashFloatId: null };

  // A source the method can no longer use must not stay silently selected — that is how the wrong
  // account reaches the server. Clear it and make the operator choose again.
  useEffect(() => {
    if (loading || sources.length === 0) return;
    const selected = value.pettyCashFloatId ?? value.bankAccountId ?? value.fundingGlAccountId;
    if (!selected) return;
    if (!sources.some(s => keyOf(s) === selected)) onChange(cleared);
  }, [sources, loading]);   // eslint-disable-line react-hooks/exhaustive-deps

  const selectedKey = value.pettyCashFloatId ?? value.bankAccountId ?? value.fundingGlAccountId ?? '';

  const handleChange = (key: string) => {
    const source = sources.find(s => keyOf(s) === key);
    if (!source) { onChange(cleared); return; }

    if (source.kind === 'BANK') {
      onChange({ bankAccountId: source.bankAccountId, fundingGlAccountId: null, pettyCashFloatId: null });
      return;
    }

    // Cash and petty cash both post through the GL account; petty cash additionally names the box.
    onChange({
      bankAccountId:      null,
      fundingGlAccountId: source.accountId,
      pettyCashFloatId:   source.kind === 'PETTY_CASH' ? source.pettyCashFloatId ?? null : null,
    });
  };

  const label = (s: EligibleFundingSource) => {
    if (s.kind === 'BANK')
      // Two bank accounts can share one GL account, so the number is what tells them apart.
      return `${s.accountCode} · ${s.accountName} — ${s.bankAccountNumber}`;

    if (s.kind === 'PETTY_CASH') {
      // The balance is part of the choice, not a detail: picking a box that cannot cover the
      // payment is refused at posting, and this is where that becomes visible.
      const held = s.availableBalance ?? 0;
      return `${s.accountCode} · ${s.accountName} — holds `
           + `${held.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${s.currencyCode}`;
    }

    return `${s.accountCode} · ${s.accountName}`;
  };

  /** Bank or cash account name first — what somebody actually recognises. */
  const primaryLabel = (s: EligibleFundingSource) => s.accountName;

  const secondaryLabel = (s: EligibleFundingSource) =>
    s.kind === 'BANK'
      // Last four digits only: enough to tell two similar accounts apart without putting a whole
      // account number on a shared screen.
      ? `${s.accountCode} · ····${(s.bankAccountNumber ?? '').slice(-4)}`
      : s.accountCode;

  const emptyText = loading
    ? 'Loading…'
    : sources.length === 0
      // Say what to do about it. "No accounts" alone leaves the operator stuck.
      ? paymentMethod === 'PettyCash'
        // A different fix from the cash/bank case: there is no account to configure, there is a
        // cash box to register.
        ? 'No petty cash float available — register one under Banking → Petty Cash'
        : 'No account set up for this method — check Chart of Accounts / Payment Methods'
      : forAp
        ? paymentMethod === 'PettyCash' ? 'Which cash box…' : 'Pay from…'
        : 'Receive into…';

  const chosen = sources.find(s => keyOf(s) === selectedKey) ?? null;

  // ── Too many to choose from: search instead ───────────────────────────────
  if (sources.length > SEARCHABLE_ABOVE) {
    const columns: SearchColumn<EligibleFundingSource & { id: string }>[] = [
      { key: 'name',   label: forAp ? 'Pay from' : 'Receive into', render: r => primaryLabel(r) },
      { key: 'detail', label: 'Account', render: r => secondaryLabel(r),
        className: 'font-mono text-xs' },
      { key: 'ccy',    label: 'CCY', render: r => r.currencyCode },
    ];

    return (
      <div className={className}>
        <button
          type="button"
          disabled={disabled || loading || sources.length === 0}
          onClick={() => setSearchOpen(true)}
          aria-label={forAp ? 'Pay from' : 'Receive into'}
          className="w-full flex items-center gap-1 rounded border border-gray-300 dark:border-gray-600
                     px-2 py-1 text-sm text-left disabled:bg-gray-100
                     hover:border-gray-400 dark:hover:border-gray-500"
        >
          <Search size={12} className="shrink-0 text-gray-400" />
          <span className={chosen ? 'truncate' : 'truncate text-gray-400'}>
            {chosen ? primaryLabel(chosen) : emptyText}
          </span>
        </button>

        {chosen && (
          <p className="mt-0.5 text-[11px] font-mono text-gray-500 dark:text-gray-400 truncate">
            {secondaryLabel(chosen)}
          </p>
        )}

        <MasterSearchModal
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
          title={forAp ? 'Pay from' : 'Receive into'}
          columns={columns}
          fetchData={(search, page, pageSize) => {
            // Filtered here rather than at the server: the eligible set is already loaded, it is
            // short enough to hold, and asking again on every keystroke would be slower than not.
            const term = search.trim().toLowerCase();
            const rows = sources
              .map(source => ({ ...source, id: keyOf(source) }))
              .filter(source => !term
                || primaryLabel(source).toLowerCase().includes(term)
                || secondaryLabel(source).toLowerCase().includes(term)
                || (source.bankAccountNumber ?? '').toLowerCase().includes(term));

            return Promise.resolve({
              items: rows.slice((page - 1) * pageSize, page * pageSize),
              totalCount: rows.length,
            });
          }}
          onSelect={row => { handleChange(row.id); setSearchOpen(false); }}
        />

        {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  }

  // ── Few enough to simply pick ─────────────────────────────────────────────
  return (
    <div className={className}>
      <select
        value={selectedKey}
        onChange={e => handleChange(e.target.value)}
        disabled={disabled || loading || sources.length === 0}
        aria-label={forAp ? 'Pay from' : 'Receive into'}
        className="w-full rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-sm disabled:bg-gray-100"
      >
        <option value="">{emptyText}</option>
        {sources.map(s => (
          <option key={keyOf(s)} value={keyOf(s)}>
            {label(s)}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
